/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, CommandButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Link } from '@fluentui/react/lib/Link';
import { IOverflowSetItemProps, IOverflowSetProps, IOverflowSetStyles, OverflowSet } from '@fluentui/react/lib/OverflowSet';
import { IStackItemStyles, IStackProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/style-utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { BrandLogo, BrandLogoProps } from 'src/appShell/components/BrandLogo';
import { GlobalHeaderLinks } from 'src/appShell/globalHeader/utils/globalHeaderLinks';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useOverflowedItems } from 'src/core/hooks/useOverflowedItems';
import { getScopedTheme, getStylistV2, joinClassNames } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';

const messages = defineMessages({
    support: {
        id: 'portal.loggedOutHeader.support',
        defaultMessage: 'Support'
    },
    forum: {
        id: 'portal.loggedOutHeader.forum',
        defaultMessage: 'Forum'
    },
    resources: {
        id: 'portal.loggedOutHeader.resources',
        defaultMessage: 'Resources'
    },
    documentation: {
        id: 'portal.loggedOutHeader.documentation',
        defaultMessage: 'Documentation'
    },
    authoringApi: {
        id: 'portal.loggedOutHeader.authoringApi',
        defaultMessage: 'Authoring API'
    },
    predictionApi: {
        id: 'portal.loggedOutHeader.predictionApi',
        defaultMessage: 'Prediction API'
    },
    pricing: {
        id: 'portal.loggedOutHeader.pricing',
        defaultMessage: 'Pricing'
    },
    azureBlog: {
        id: 'portal.loggedOutHeader.azureBlog',
        defaultMessage: 'Azure Blog'
    },
    signIn: {
        id: 'portal.loggedOutHeader.signIn',
        defaultMessage: 'Sign in'
    },
    moreItemsAriaLabel: {
        id: 'portal.loggedOutHeader.moreItemsAriaLabel',
        defaultMessage: 'More items'
    }
});

const headerTheme = getScopedTheme('LoggedOutHeader');
const { getClassName, styleComponent } = getStylistV2('LoggedOutHeader');

const headerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: {
        backgroundColor: headerTheme.backgroundColor,
        minHeight: headerTheme.maxHeight,
        maxHeight: headerTheme.maxHeight,
        margin: headerTheme.horizontalPadding
    }
});
const headerButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { height: '100%', backgroundColor: headerTheme.backgroundColor, color: headerTheme.textColor, overflow: 'auto' },
    rootHovered: { backgroundColor: headerTheme.backgroundColorHovered, color: headerTheme.textColor },
    rootExpanded: { backgroundColor: headerTheme.backgroundColorActive, color: headerTheme.textColor },
    icon: { padding: headerTheme.iconPadding, color: headerTheme.textColor },
    iconHovered: { color: headerTheme.textColor },
    iconPressed: { color: headerTheme.textColor }
});

const logoLinkClassName = getClassName('LogoLinkStyles', theme => ({ display: 'flex', height: theme.logoLinkHeight }));
const overflowSetStyles = classNamesFunction<IOverflowSetProps, IOverflowSetStyles>()({ root: { height: '100%' } });
const headerButtonFocusClassName = mergeStyles(getFocusStyle(getTheme(), { highContrastStyle: { outlineOffset: '-8px' } }));
const logoFocusClassNames = mergeStyles(getFocusStyle(getTheme()));

const AzureLogo = styleComponent<BrandLogoProps>(BrandLogo)('AzureLogo', theme => ({
    width: theme.logoContainerWidth,
    padding: theme.logoPadding
}));

/**
 * Header displayed in the logged out state.
 */
export const LoggedOutHeader = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { uiStore, dispatcher } = useGlobalState();

    const isLanguageStudio = uiStore.portal.kind === 'languageStudio';
    const signIn = React.useCallback(() => dispatcher.dispatch('login'), [dispatcher]);

    const items: IOverflowSetItemProps[] = [
        {
            target: '_blank',
            key: 'cognitiveServices',
            name: 'Cognitive Services',
            rel: 'noopener noreferrer',
            href: GlobalHeaderLinks.cognitiveServicesLink
        },
        ...(!isLanguageStudio
            ? [
                  {
                      key: 'languageUnderstanding',
                      name: 'Language Understanding',
                      onClick: () => history.push(routeTable.root())
                  },
                  {
                      key: 'support',
                      name: localize(messages.support),
                      subMenuProps: {
                          items: [
                              {
                                  target: '_blank',
                                  key: 'stackOverflow',
                                  text: 'Stack Overflow',
                                  rel: 'noopener noreferrer',
                                  style: { textDecoration: 'underline' },
                                  href: GlobalHeaderLinks.stackOverflowLink
                              },
                              {
                                  target: '_blank',
                                  key: 'supportPlans',
                                  rel: 'noopener noreferrer',
                                  text: 'Azure Support Plans',
                                  style: { textDecoration: 'underline' },
                                  href: GlobalHeaderLinks.azureSupportLink
                              },
                              {
                                  key: 'forum',
                                  target: '_blank',
                                  rel: 'noopener noreferrer',
                                  text: localize(messages.forum),
                                  style: { textDecoration: 'underline' },
                                  href: GlobalHeaderLinks.loggedOutForumLink
                              }
                          ]
                      }
                  },
                  {
                      key: 'resources',
                      name: localize(messages.resources),
                      subMenuProps: {
                          items: [
                              {
                                  target: '_blank',
                                  key: 'documentation',
                                  rel: 'noopener noreferrer',
                                  text: localize(messages.documentation),
                                  style: { textDecoration: 'underline' },
                                  href: GlobalHeaderLinks.documentationHelpLink
                              },
                              {
                                  target: '_blank',
                                  key: 'authoringApi',
                                  rel: 'noopener noreferrer',
                                  text: localize(messages.authoringApi),
                                  style: { textDecoration: 'underline' },
                                  href: GlobalHeaderLinks.authoringAPIsHelpLink
                              },
                              {
                                  target: '_blank',
                                  key: 'predictionApi',
                                  rel: 'noopener noreferrer',
                                  text: localize(messages.predictionApi),
                                  style: { textDecoration: 'underline' },
                                  href: GlobalHeaderLinks.predictionAPIsHelpLink
                              },
                              {
                                  key: 'pricing',
                                  target: '_blank',
                                  rel: 'noopener noreferrer',
                                  text: localize(messages.pricing),
                                  href: GlobalHeaderLinks.pricingLink,
                                  style: { textDecoration: 'underline' }
                              },
                              {
                                  key: 'blog',
                                  target: '_blank',
                                  rel: 'noopener noreferrer',
                                  text: localize(messages.azureBlog),
                                  style: { textDecoration: 'underline' },
                                  href: GlobalHeaderLinks.microsoftAzureBlogLink
                              }
                          ]
                      }
                  }
              ]
            : [])
    ];

    const onRenderItem = (item: IOverflowSetItemProps) => (
        <CommandButton
            key={item.key}
            rel={item.rel as string}
            href={item.href as string}
            text={item.name as string}
            styles={headerButtonStyles}
            target={item.target as string}
            className={headerButtonFocusClassName}
            menuProps={item.subMenuProps as { items: IOverflowSetItemProps[] }}
        />
    );

    /**
     * Renders the more button when the overflow set
     * hides items due to small screen size.
     *
     * @param overflowItems The items to render in the more
     * button overflow.
     */
    const onRenderMoreButton = (overflowItems: IOverflowSetItemProps[]) => (
        <CommandBarButton
            role="menuitem"
            title={localize(messages.moreItemsAriaLabel)}
            styles={headerButtonStyles}
            className={headerButtonFocusClassName}
            menuIconProps={{ iconName: 'More' }}
            menuProps={{ items: overflowItems }}
        />
    );

    const { items: renderedItems, overFlowItems } = useOverflowedItems<IOverflowSetItemProps>(items);

    return (
        <Stack
            styles={headerStyles}
            verticalAlign="center"
            as="nav"
            tokens={{ childrenGap: 15 }}
            horizontal
            horizontalAlign="space-between"
            verticalFill>
            <Stack verticalFill horizontal verticalAlign="center">
                <Link
                    as="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Microsoft azure link"
                    href={GlobalHeaderLinks.microsoftAzureLink}
                    className={joinClassNames(logoLinkClassName, logoFocusClassNames)}>
                    <AzureLogo
                        defaultLogo="blackLogo"
                        blackLogoBlobPath="assets/images/logos/microsoftBlackHeaderLogo.svg"
                        whiteLogoBlobPath="assets/images/logos/microsoftWhiteHeaderLogo.svg"
                        alt="Microsoft"
                    />
                </Link>

                <OverflowSet
                    items={renderedItems}
                    onRenderItem={onRenderItem}
                    styles={overflowSetStyles}
                    overflowItems={overFlowItems}
                    onRenderOverflowButton={onRenderMoreButton}
                />
            </Stack>

            <StackItem verticalFill align="end">
                <CommandButton styles={headerButtonStyles} key="signIn" text={localize(messages.signIn)} onClick={signIn} />
            </StackItem>
        </Stack>
    );
});
